import type * as React from 'react';

import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import type { FlashcardsConfig } from './flashcards/types';

/**
 * A native widget renders a registered plugin (an `ungradedWidget`) as an in-app React
 * component instead of an iframe.
 * The item's config arrives on the widget session
 * (`session.configuration`) and carries a `widgetRenderConfigurationType` discriminator
 * identifying the renderer. The field is intentionally verbose to avoid colliding with
 * arbitrary keys authors may include in their `manifest.json`.
 */
export type NativeWidgetConfigBase = {
  widgetRenderConfigurationType: string;
};

/**
 * Discriminated union of every registered native widget config.
 * Extend by adding the new plugin's config to the union as new native widgets are registered.
 */
export type NativeWidgetConfig = FlashcardsConfig;

export type NativeWidgetRendererBaseProps<T extends NativeWidgetConfigBase> = {
  config: T;
  isComplete?: boolean;
  markComplete?: () => void;
  markStarted?: () => void;
  /** Next item metadata, used by renderers to offer a "Go to next item" affordance. */
  nextItem?: ItemMetadata;
  showPopupButton?: boolean;
  renderCustomContainer?: (onExpand: () => void) => React.ReactNode;
};

export type NativeWidgetRenderer<T extends NativeWidgetConfigBase = NativeWidgetConfigBase> = React.ComponentType<
  NativeWidgetRendererBaseProps<T>
>;

export function isNativeWidgetConfig(config: unknown): config is NativeWidgetConfig {
  return typeof config === 'object' && config !== null && 'widgetRenderConfigurationType' in config;
}
